import React, { useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import frLocale from "@fullcalendar/core/locales/fr";
import { FaCalendarPlus } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useGetCalendarEvents } from "../../hooks/CALENDAR/useGetCalendarEvents";
import NewAgendaEvent from "../../components/Modals/Create/NewAgendaEvent";
import EditAgendaEvent from "../../components/Modals/Update/EditAgendaEvent";

// Retourne un tableau d'événements FullCalendar.
// Les tickets multi-assignés produisent un événement par user (couleur distincte).
function mapToFullCalendarEvents(event) {
  let title = event.label || event.description || "(sans titre)";
  if (event.type === "ticket_intervention" && event.ticket) {
    title = `🎫 ${event.ticket.title}`;
  } else if (event.type === "project_intervention" && event.project) {
    title = `📁 ${event.project.title}`;
  } else if (event.type === "ticket") {
    title = `🎫 ${event.label || event.description || "(sans titre)"}`;
  }

  const base = {
    title,
    start: event.start_date,
    end: event.end_date || undefined,
    extendedProps: { original: event },
  };

  if (event.type === "ticket" && event.users?.length) {
    return event.users.map((u) => ({
      ...base,
      id: `ticket-${event.id}-user-${u.id}`,
      backgroundColor: u.color || "#4e73df",
      borderColor: u.color || "#4e73df",
      extendedProps: { original: event, userId: u.id },
    }));
  }

  const color = event.user?.color || "#4e73df";
  return [{
    ...base,
    id: `${event.type}-${event.id}`,
    backgroundColor: color,
    borderColor: color,
  }];
}

const Calendrier = ({ titre }) => {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const calendarRef = useRef(null);

  const restored = location.state;

  const [range, setRange] = useState({ start: null, end: null });
  const [showCreate, setShowCreate] = useState(false);
  const [createDefaultDate, setCreateDefaultDate] = useState("");
  const [editEvent, setEditEvent] = useState(null);
  const [hiddenUserIds, setHiddenUserIds] = useState(new Set());

  const { data, isLoading } = useGetCalendarEvents(range.start, range.end);

  const legendUsers = useMemo(() => {
    const map = new Map();
    (data?.data?.events || []).forEach((ev) => {
      // "ticket" a users[], les autres types ont user
      const users = ev.users?.length ? ev.users : (ev.user ? [ev.user] : []);
      users.forEach((u) => {
        if (u && !map.has(u.id)) {
          map.set(u.id, { id: u.id, name: u.name, surname: u.surname, color: u.color || "#4e73df" });
        }
      });
    });
    return Array.from(map.values());
  }, [data]);

  const events = useMemo(
    () =>
      (data?.data?.events || []).flatMap((ev) => {
        // Pour les tickets multi-users, on filtre par user avant de dupliquer
        if (ev.users?.length) {
          const visibleUsers = ev.users.filter((u) => !hiddenUserIds.has(u.id));
          if (!visibleUsers.length) return [];
          return mapToFullCalendarEvents({ ...ev, users: visibleUsers });
        }
        if (hiddenUserIds.has(ev.user?.id)) return [];
        return mapToFullCalendarEvents(ev);
      }),
    [data, hiddenUserIds]
  );

  const toggleUser = (userId) => {
    setHiddenUserIds((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const handleDatesSet = useCallback((dateInfo) => {
    const start = dateInfo.startStr.slice(0, 10);
    const end = dateInfo.endStr.slice(0, 10);
    setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  const handleDateClick = (info) => {
    if (!hasPermission("CREATE_EVENT")) return;
    setCreateDefaultDate(info.dateStr);
    setShowCreate(true);
  };

  const handleEventClick = (info) => {
    const original = info.event.extendedProps.original;
    const api = calendarRef.current?.getApi();
    const calendarState = api
      ? { calendarDate: api.getDate().toISOString(), calendarView: api.view.type }
      : {};
    const navState = {
      state: {
        from: location.pathname,
        listState: { scrollY: window.scrollY, ...calendarState },
      },
    };
    if (original.type === "ticket_intervention" && original.ticket?.id) {
      navigate(`/ticket/${original.ticket.id}`, navState);
      return;
    }
    if (original.type === "ticket") {
      navigate(`/ticket/${original.id}`, navState);
      return;
    }
    if (original.type === "project_intervention" && original.project?.id) {
      navigate(`/projet/${original.project.id}`, navState);
      return;
    }
    if (original.type === "diary") {
      const currentUserId = user?.data?.id;
      if (original.user?.id !== currentUserId) return;
      setEditEvent(original);
    }
  };

  return (
    <>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">{titre}</h1>
        {hasPermission("CREATE_EVENT") && (
          <button
            className="btn btn-primary btn-icon-split"
            onClick={() => { setCreateDefaultDate(""); setShowCreate(true); }}
          >
            <span className="icon body-color">
              <FaCalendarPlus />
            </span>
            <span className="text">Ajouter un événement</span>
          </button>
        )}
      </div>

      {legendUsers.length > 0 && (
        <div className="card shadow-sm mb-3">
          <div className="card-body py-2 px-3">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <small className="text-muted fw-semibold me-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Utilisateurs
              </small>
              {legendUsers.map((u) => {
                const hidden = hiddenUserIds.has(u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() => toggleUser(u.id)}
                    className="btn btn-sm d-flex align-items-center gap-1"
                    style={{
                      borderColor: u.color,
                      color: hidden ? "var(--bs-secondary-color)" : u.color,
                      backgroundColor: hidden ? "transparent" : `${u.color}1a`,
                      opacity: hidden ? 0.5 : 1,
                      fontSize: "0.78rem",
                      padding: "0.2rem 0.55rem",
                      transition: "all 0.15s",
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        backgroundColor: hidden ? "transparent" : u.color,
                        border: `2px solid ${u.color}`,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    {u.surname} {u.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="card border-left-primary shadow mb-4">
        <div className="card-body p-3">
          {isLoading && (
            <div className="text-center text-muted py-2 small">Chargement des événements…</div>
          )}
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            locale={frLocale}
            initialView={restored?.calendarView || "timeGridWeek"}
            initialDate={restored?.calendarDate || undefined}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            events={events}
            datesSet={handleDatesSet}
            dateClick={hasPermission("CREATE_EVENT") ? handleDateClick : undefined}
            eventClick={handleEventClick}
            nowIndicator
            hiddenDays={[0]}
            allDaySlot={false}
            slotMinTime="08:00:00"
            slotMaxTime="19:00:00"
            height="auto"
            eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
            slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          />
        </div>
      </div>

      <NewAgendaEvent
        showNewAgendaEvent={showCreate}
        setShowNewAgendaEvent={setShowCreate}
        defaultDate={createDefaultDate}
      />

      <EditAgendaEvent
        event={editEvent}
        show={!!editEvent}
        onHide={() => setEditEvent(null)}
      />
    </>
  );
};

export default Calendrier;
